// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import { Local } from '@ta/overlays.managers';
import { Overlay, ShadowBox } from '@ta/overlays.pieces';
import classNames from '@ta/common.classnames';
import HoverChain from '@ta/common.hover-chain';
import type { HoverChainNode } from '@ta/common.hover-chain';
import MenuTrigger from './MenuTrigger';
import styles from './Menu.less';
import type { NavLinkType, TabType } from '../types';

export type Position = {
  position: string,
  top: string,
  right: string,
  left: string,
};

type ExternalProps = {|
  /** Whether or not the trigger (i.e., the ui_tab element) should show as active */
  triggerActive: boolean,

  /** Whether this is a subment */
  submenu?: boolean,

  /** Let the parent know whether the menu is open or not */
  menuStateCallback?: boolean => void,

  /** The function that renders item text */
  itemText?: (() => void, () => void, () => void, () => void) => React.Node,

  /** Menu contents */
  children: (?HoverChainNode, any) => React.Node,

  /** Whether the tab is a link or a non-link */
  tabType?: TabType,

  /** Data model for the link, if TabType === link */
  link?: NavLinkType,

  /** Data model for the tab contents, if TabType === nonlink, e.g. for the ellipsis menu which is a span */
  nonLinkContents?: React.Node,

  /** Whether the menu is aside its parent component. The default position is at the bottom */
  isAside?: boolean,

  /** Pointer to a parent node. Optional */
  hoverChainParent?: HoverChainNode,

  /** The top position of menu */
  topLevelPosition?: Position,
|};

type Props = {|
  ...ExternalProps,
  isRtl?: boolean,
|};

const getName: () => string = (() => {
  let i = 0;
  return () => {
    i += 1;
    return `nav_menu_${i}`;
  };
})();

/**
 * Creates a trigger and a menu.
 */
class Menu extends React.Component<Props> {
  static defaultProps = {
    triggerActive: false,
  };

  constructor(props: Props) {
    super(props);
    this.name = getName();
  }

  getOverlayPosition = (triggerRef: HTMLElement) => {
    const triggerEl = triggerRef;
    const widthAdjustment = this.props.isAside ? triggerRef.offsetWidth : 0;
    const heightAdjustment = this.props.isAside ? 0 : triggerRef.offsetHeight;
    /* eslint-disable no-restricted-globals */
    let top;
    if (this.props.topLevelPosition?.top) {
      top = this.props.topLevelPosition?.top;
    } else {
      top = triggerEl ? `${heightAdjustment + triggerEl.getBoundingClientRect().top}px` : '0px';
    }
    let left;
    let right;
    if (this.props.isRtl && !this.props.isAside) {
      left = 'auto';
      const bodyWidth =
        document && document.documentElement ? document.documentElement.clientWidth : 0;
      right = triggerEl
        ? `${bodyWidth - triggerEl.getBoundingClientRect().right - window.pageXOffset}px`
        : '0px';
    } else if (!this.props.isRtl && !this.props.isAside) {
      left = triggerEl ? `${triggerEl.getBoundingClientRect().left + window.pageXOffset}px` : '0px';
      right = 'auto';
    } else if (this.props.isRtl && this.props.isAside) {
      left = 'auto';
      const bodyWidth =
        document && document.documentElement ? document.documentElement.clientWidth : 0;
      right = triggerEl
        ? `${bodyWidth -
            triggerEl.getBoundingClientRect().right +
            widthAdjustment -
            window.pageXOffset}px`
        : '0px';
    } else {
      left = triggerEl
        ? `${triggerEl.getBoundingClientRect().left + widthAdjustment + window.pageXOffset}px`
        : '0px';
      right = 'auto';
    }

    return {
      position: 'fixed',
      top,
      left,
      right,
    };
  };

  name: string;

  render() {
    return (
      <MenuTrigger
        name={this.name}
        tabType={this.props.tabType}
        link={this.props.link}
        nonLinkContents={this.props.nonLinkContents}
        triggerActive={this.props.triggerActive}
        hoverChainParent={this.props.hoverChainParent}
        itemText={this.props.itemText ? this.props.itemText : null}
        menuStateCallback={this.props.menuStateCallback ? this.props.menuStateCallback : null}
      >
        {(triggerRef, hoverChainParent) => (
          <React.Fragment>
            {/** Important for SEO: this line will render menu contents in a hidden div for crawlers to see what would be in the dropdown. */}
            <div className={styles.dropdownContents}>{this.props.children()}</div>

            <Local name={this.name}>
              <HoverChain hoverChainParent={hoverChainParent} mouseOutTimeout={0}>
                {(setTarget, ptr) => {
                  const overlayPosition = this.getOverlayPosition(triggerRef);
                  return (
                    <Overlay position={overlayPosition}>
                      <div
                        ref={setTarget}
                        className={classNames(styles.overlayWrap, {
                          [styles.submenu]: !!this.props.submenu,
                        })}
                      >
                        {this.props.submenu ? (
                          <div className={styles.menuContentsWrap}>
                            {this.props.children(ptr, overlayPosition)}
                          </div>
                        ) : (
                          <ShadowBox>
                            <div className={styles.menuContentsWrap}>
                              {this.props.children(ptr, overlayPosition)}
                            </div>
                          </ShadowBox>
                        )}
                      </div>
                    </Overlay>
                  );
                }}
              </HoverChain>
            </Local>
          </React.Fragment>
        )}
      </MenuTrigger>
    );
  }
}

export default connect<Props, ExternalProps, _, _, _, _>(state => ({
  isRtl: state.i18n.isRtl,
}))(Menu);
